import { toJS } from "mobx";
import { observer } from "mobx-react";
import React, { useEffect, useState } from "react";
import { Redirect, useParams } from "react-router-dom";
import { useLanguageContext, useWebGalleryStore } from "../../../hooks";
import AssetViewer from "../../../lib/AssetViewer";
import { GalleryState, IAssetGallery } from "../../../stores/WebGalleryStore";
import { ErrorTypes } from "../../../types";
import { IWebGalleryUrlParams } from "../../App";
import Page from "../../Page";
import { GalleryEmpty } from "../../partials/GalleryEmpty";
import { GalleryError } from "../../partials/GalleryError";
import { GalleryLoader } from "../../partials/GalleryLoader";
import { GalleryRequested } from "../../partials/GalleryRequested";
import { DetailMeta } from "./DetailMeta";


const WebDetail: React.FunctionComponent = () => {
  const { galleryId, assetId } = useParams<IWebGalleryUrlParams>();
  const store = useWebGalleryStore();
  const [isFetching, setIsFetching] = useState(false);
  const [isIntervalRunning, setIsIntervalRunning] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const { lng } = useLanguageContext();

  useEffect(() => {
    if (!store.gallery) {
      setIsFetching(true);
      setError(null);
      store
        .fetchGalleryById(galleryId)
        .then((gallery) => toJS(gallery))
        .then(() => {
          // gallery is fetched
          if (assetId) {
            return store.selectAssetById(assetId);
          }
        })
        .catch((error) => {
          if (!error.message.includes(ErrorTypes.API_NOT_CONFIGFURED)) {
            console.error(error);
          }
          setError(error);
        })
        .finally(() => {
          setIsFetching(false);
        });
    }
  }, [galleryId, store.api.endpoint, assetId, store]);

  useEffect(() => {
    store.selectAssetById(assetId).catch((err) => {
      console.error(err);
      setError(err);
    });
  }, [assetId, store]);

  if (error) {
    if (error.message.includes(ErrorTypes.UNAUTHORIZED)) {
      return <Redirect to={`/${galleryId}-ExternalUser/${assetId}/authorize`} />;
    } else if (error.message.includes(ErrorTypes.API_NOT_CONFIGFURED)) {
      return <GalleryLoader />;
    } else {
      return (
        <Page>
          <GalleryError />
        </Page>
      );
    }
  }

  // show loading indicator while fetching
  if (isFetching || !store.gallery) {
    return <GalleryLoader />;
  }

    // gallery has specific requested state
  if (!isFetching && store.gallery?.state === GalleryState.REQUESTED && store?.assets?.length === 0) {
      return (
        <Page>
          <GalleryRequested />
        </Page>
      );
  }

  // show not found or removed message when empty
  if (!isFetching && store.assets.length === 0) {
    return (
      <Page>
        <GalleryEmpty />
      </Page>
    );
  }

  // on success, default to rendered gallery detail
  return <WebDetailPage asset={store.selectedAsset} gallery={store.gallery} disableThumbnails={isIntervalRunning} lng={lng} />;
};

type WebDetailPageProps = {
  gallery: IAssetGallery | undefined;
  asset: Asset | undefined;
  disableThumbnails?: boolean;
  lng?: string;
};

const WebDetailPage = ({ asset, gallery, disableThumbnails, lng }: WebDetailPageProps) => {
  return (
    <Page scrollable={false} disableThumbnails={disableThumbnails}>
      <div className="detail__content-wrapper">
        <div className="detail__content">
          <AssetViewer asset={asset} lng={lng} />
        </div>
        <DetailMeta asset={asset} gallery={gallery} />
      </div>
    </Page>
  );
};

export default observer(WebDetail);
